import {
  type IGetActionsData,
  type TGetCommunicationTemplates,
  formatSocialOfferForApi,
  usePatchSocialApply,
  usePostSocialApply,
  useSendFeedbackToData,
} from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { IChallenge } from "@bbj-types/challenge";
import { CommunicationTemplateType } from "@bbj-types/communication/enum";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ISocialApplyTransition } from "@bbj-types/socialApply";
import { ProposalType, SocialApplyStatus } from "@bbj-types/socialApply/enum";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGlobalContext } from "@root/context";
import type { IEmailFormTemplate } from "@styleguide";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { type TSendSocialOfferResolver, sendSocialOfferValidationSchema } from "./resolver";
import { defaultMode, formatCommunicationData } from "./utils";

export const useSendSocialOfferModal = (
  offer: ISocialOffer & {
    status?: SocialApplyStatus;
    transitions?: ISocialApplyTransition[];
    socialApplyId?: string;
  },
  candidate: ICandidate,
  challenges: IChallenge[],
  onSuccess: (
    socialApply: { _id: string; status: string; transitions: ISocialApplyTransition[] },
    action: IGetActionsData
  ) => void,
  onClose: () => void,
  getTemplates: () => TGetCommunicationTemplates
) => {
  const { user } = useGlobalContext();
  const [mode, setMode] = useState<CommunicationTemplateType>(defaultMode(candidate));
  const [{ loading: loadingSendFeedBactToData }, sendFeedbackToData] = useSendFeedbackToData();
  const [{ loading: loadingPostSocialApply }, postSocialApply] = usePostSocialApply();
  const [{ loading: loadingPatchSocialApply }, patchSocialApply] = usePatchSocialApply();
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  const templatesApi = getTemplates();

  const templates = useMemo(() => {
    const emailTemplates = templatesApi.filter(
      (t) => t.type === CommunicationTemplateType.EMAIL
    ) as IEmailFormTemplate[];
    const smsTemplates = templatesApi.filter((t) => t.type === CommunicationTemplateType.SMS);
    return { emailTemplates, smsTemplates };
  }, [templatesApi]);

  const initialTemplate = useMemo(() => {
    const currentTemplates =
      mode === CommunicationTemplateType.EMAIL ? templates.emailTemplates : templates.smsTemplates;
    return currentTemplates[0];
  }, [templates, mode]);

  const { control, handleSubmit, setValue } = useForm<TSendSocialOfferResolver>({
    resolver: yupResolver(sendSocialOfferValidationSchema),
    defaultValues: {
      mode,
      template: initialTemplate?._id ?? null,
      templateType: initialTemplate?.name,
      emails: candidate.mail ? [{ address: candidate.mail, name: `${candidate.firstName} ${candidate.lastName}` }] : [],
      cc: [],
      subject: mode === CommunicationTemplateType.EMAIL ? (initialTemplate?.subject ?? "") : "",
      attachments: [],
      content: initialTemplate?.content ?? "",
    },
    mode: "onSubmit",
  });

  const handleModeChange = useCallback(
    (newMode: CommunicationTemplateType) => {
      setMode(newMode);
      setValue("mode", newMode);
      const newTemplates = templatesApi?.filter((t) => t.type === newMode) ?? [];
      setValue("template", newTemplates[0]?._id ?? "");
      setValue("templateType", newTemplates[0]?.name ?? "");
      setValue("content", newTemplates[0]?.content ?? "");
      if (newMode === CommunicationTemplateType.EMAIL) {
        setValue("subject", newTemplates[0]?.subject ?? "");
      }
    },
    [setValue, templatesApi]
  );

  const sendProposalFeedback = useCallback(
    async (mode?: CommunicationTemplateType) => {
      await sendFeedbackToData({
        candidate: candidate._id,
        event: "proposed",
        kind: "social",
        recommendations: [offer.offerId],
      });

      window.publish("feedback", {
        severity: "success",
        message: `L'offre a été proposée au candidat par ${mode === CommunicationTemplateType.EMAIL ? "email" : "SMS"}`,
      });
    },
    [candidate._id, offer.offerId, sendFeedbackToData]
  );

  const handleCreateSocialApply = useCallback(
    async (data: TSendSocialOfferResolver) => {
      try {
        const result = await postSocialApply({
          candidate: candidate._id,
          organisation: user.organisation._id,
          source: offer.source,
          socialOffer: formatSocialOfferForApi(offer),
          challenges: challenges.map((challenge) => challenge._id),
          status: SocialApplyStatus.PROPOSED,
          proposal: {
            type: data.mode === CommunicationTemplateType.EMAIL ? ProposalType.EMAIL : ProposalType.SMS,
            communicationData: formatCommunicationData(candidate, data),
          },
        });

        await sendProposalFeedback(data.mode);
        onSuccess(result.data.socialApply[0], result.data.action);
      } catch (error) {
        window.publish("feedback", {
          severity: "error",
          message: "Une erreur est survenue lors de l'envoi de l'offre",
        });
      }
    },
    [candidate, user, offer, challenges, postSocialApply, sendFeedbackToData, onSuccess]
  );

  const handleUpdateSocialApply = useCallback(
    async (data: TSendSocialOfferResolver, socialApplyId: string) => {
      try {
        await patchSocialApply(socialApplyId, {
          status: SocialApplyStatus.PROPOSED,
          proposal: {
            type: data.mode === CommunicationTemplateType.EMAIL ? ProposalType.EMAIL : ProposalType.SMS,
            communicationData: formatCommunicationData(candidate, data),
          },
        });

        await sendProposalFeedback(data.mode);
      } catch (error) {
        window.publish("feedback", {
          severity: "error",
          message: "Une erreur est survenue lors de l'envoi de l'offre",
        });
      }
    },
    [candidate, user, offer, challenges, postSocialApply, sendProposalFeedback, onSuccess]
  );

  const onSubmit = useCallback(
    async (data: TSendSocialOfferResolver) => {
      try {
        if (offer.socialApplyId) {
          await handleUpdateSocialApply(data, offer.socialApplyId);
        } else {
          await handleCreateSocialApply(data);
        }
        onClose();
      } catch (error) {
        window.publish("feedback", {
          severity: "error",
          message: "Une erreur est survenue lors de l'envoi de l'offre",
        });
      }
    },
    [offer.socialApplyId, handleUpdateSocialApply, handleCreateSocialApply, onClose]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return {
    handleClose,
    templates,
    mode,
    setMode,
    loading: loadingPostSocialApply || loadingSendFeedBactToData || loadingPatchSocialApply,
    handleSubmit,
    onSubmit,
    control,
    setValue,
    handleModeChange,
    attachmentsLoading,
    setAttachmentsLoading,
  };
};
